import * as React from 'react';
import * as ReactDOM from 'react-dom';
import {
  AssistantPositionX,
  AssistantPositionY,
  AlignPosition,
} from '../../types/assistant-actions';
import { Deferred } from '../../logic/deferred';
import { TourTooltipWrapper } from '../react/tooltip/TourTooltipWrapper';

//%% move to a more generic place for ui
const DEFAULT_POSITION_DURATION_MS = 500;
const ELEMENT_TRANSITION_ACTIVE_CLASS = 'tour-element-transition-active';
const REACT_CONTAINER_ID = 'TourReactContainer';

let reactContainer = null;

const getReactContainer = () => {
  if (!reactContainer) {
    reactContainer = document.getElementById(REACT_CONTAINER_ID);
  }

  return reactContainer;
};

export const addReactContainer = () => {
  const rootElement = document.createElement('div');
  rootElement.id = REACT_CONTAINER_ID;
  document.body.appendChild(rootElement);

  return ReactDOM.render(React.createElement(TourTooltipWrapper), rootElement);
};

export const addReactComponentToDom = (component: React.ReactElement) => {
  ReactDOM.render(component, getReactContainer());
};

export const removeReactComponentFromDom = (component: React.ReactElement) => {
  ReactDOM.unmountComponentAtNode(getReactContainer());
};

export const outerPositionElement = (
  element: HTMLElement,
  target: HTMLElement,
  positionX: AssistantPositionX,
  positionY: AssistantPositionY,
  durationInMs: number = DEFAULT_POSITION_DURATION_MS,
  fitToScreen: boolean = true,
  offsetX?: number,
  offsetY?: number,
  minY?: number,
  alignTo?: AlignPosition,
): Promise<void> => {
  const defer = new Deferred<void>();
  try {
    if (!target) {
      element.style.display = 'none';
      defer.resolve();
    } else {
      const oldOffset = element.getBoundingClientRect();
      let newOffset = getOuterPositionOffset(
        element,
        target,
        positionX,
        positionY,
        offsetX,
        offsetY,
        alignTo,
      );
      const style = window.getComputedStyle(element);
      const hasPreviousPosition = !(
        style.left === 'auto' || style.top === 'auto'
      );

      if (fitToScreen) {
        // for outerwidth calculation purposes
        element.style.left = '0';
        newOffset = fitOffsetToScreen(
          newOffset,
          element.offsetWidth,
          element.offsetHeight,
        );
      }

      const durationInMsString = durationInMs.toString();
      element.style.transition =
        'left ' +
        durationInMsString +
        'ms ease-in-out, top ' +
        durationInMsString +
        'ms ease-in-out';
      if (newOffset.top < minY) {
        newOffset.top = minY;
      }

      // if (
      //   this.flags.uAssistantInPanel &&
      //   this.$injector.get<AssistantManager>('assistantManager').isMenuOpen()
      // ) {
      //   const topBarHeight: number = 66; // $top-bar-height and $top-bar-height-old
      //   element.css({ right: 0, top: topBarHeight, left: 'auto' });
      // } else {
      element.style.top = `${newOffset.top}px`;
      element.style.left = `${newOffset.left}px`;
      element.style.position = 'absolute';
      //}
      if (
        durationInMs === 0 ||
        (oldOffset.left === newOffset.left &&
          oldOffset.top === newOffset.top) ||
        !hasPreviousPosition
      ) {
        element.classList.remove(ELEMENT_TRANSITION_ACTIVE_CLASS);
        defer.resolve();
      } else {
        element.classList.add(ELEMENT_TRANSITION_ACTIVE_CLASS);
        element.addEventListener(
          'transitionend',
          () => {
            element.classList.remove(ELEMENT_TRANSITION_ACTIVE_CLASS);
            defer.resolve();
          },
          { once: true },
        );
      }
    }
  } catch (e) {
    console.error(e);
    defer.resolve();
  }

  return defer.promise;
};

const getInnerPositionOffset = (
  element: HTMLElement,
  target: HTMLElement,
  positionX: AssistantPositionX = AssistantPositionX.Center,
  positionY: AssistantPositionY = AssistantPositionY.Center,
) => {
  const targetBoundingClientRect = target.getBoundingClientRect();
  const offset: any = {};

  if (positionX === AssistantPositionX.Left) {
    offset.left = targetBoundingClientRect.left;
  } else if (positionX === AssistantPositionX.Center) {
    offset.left =
      targetBoundingClientRect.left +
      targetBoundingClientRect.width / 2 -
      element.offsetWidth / 2;
  } else if (positionX === AssistantPositionX.Right) {
    offset.left = targetBoundingClientRect.right - element.offsetWidth;
  }

  if (positionY === AssistantPositionY.Top) {
    offset.top = targetBoundingClientRect.top;
  } else if (positionY === AssistantPositionY.Center) {
    offset.top =
      targetBoundingClientRect.top +
      targetBoundingClientRect.height / 2 -
      element.offsetHeight / 2;
  } else if (positionY === AssistantPositionY.Bottom) {
    offset.top = targetBoundingClientRect.bottom - element.offsetHeight;
  }

  return offset;
};

export const innerPositionElement = async (
  element: HTMLElement,
  target: HTMLElement,
  positionX: AssistantPositionX,
  positionY: AssistantPositionY,
  duration: number = DEFAULT_POSITION_DURATION_MS,
) => {
  const defer = new Deferred();
  try {
    const oldOffset = element.getBoundingClientRect();
    const newOffset = getInnerPositionOffset(
      element,
      target,
      positionX,
      positionY,
    );
    const style = window.getComputedStyle(element);
    const hasPreviousPosition = !(
      style.left === 'auto' || style.top === 'auto'
    ); // should be out of the function
    const durationInSecs = duration / 1000;

    if (
      duration === 0 ||
      (oldOffset.left === newOffset.left && oldOffset.top === newOffset.top) ||
      !hasPreviousPosition
    ) {
      element.classList.remove(ELEMENT_TRANSITION_ACTIVE_CLASS);
      defer.resolve();
    } else {
      element.classList.remove(ELEMENT_TRANSITION_ACTIVE_CLASS);
      element.addEventListener(
        'transitionend',
        () => {
          element.classList.remove(ELEMENT_TRANSITION_ACTIVE_CLASS);
          defer.resolve();
        },
        { once: true },
      );
      element.classList.add(ELEMENT_TRANSITION_ACTIVE_CLASS);
    }

    element.style.transition =
      'left ' +
      durationInSecs.toString() +
      's ease-in-out, top ' +
      durationInSecs.toString() +
      's ease-in-out';
    element.style.top = `${newOffset.top}px`;
    element.style.left = `${newOffset.left}px`;
  } catch (e) {
    console.error('error', e);
    element.classList.remove(ELEMENT_TRANSITION_ACTIVE_CLASS);
    defer.resolve();
  }

  return defer.promise;
};

const isSnappingToHorizontalSides = (alignTo?: AlignPosition) =>
  alignTo === AlignPosition.Right || alignTo === AlignPosition.Left;

const isSnappingToVerticalSides = (alignTo?: AlignPosition) =>
  alignTo === AlignPosition.Top || alignTo === AlignPosition.Bottom;

const isPositionTopOrLeft = (
  position: AssistantPositionX | AssistantPositionY,
) =>
  position === AssistantPositionX.Left || position === AssistantPositionY.Top;

const alignPositionIsTheSameAsTheOpeningDirection = (
  openningPosition: AssistantPositionX | AssistantPositionY,
  alignPosition: AlignPosition,
) => {
  return (
    (openningPosition === AssistantPositionX.Right &&
      alignPosition === AlignPosition.Right) ||
    (openningPosition === AssistantPositionX.Left &&
      alignPosition === AlignPosition.Left) ||
    (openningPosition === AssistantPositionY.Top &&
      alignPosition === AlignPosition.Top) ||
    (openningPosition === AssistantPositionY.Bottom &&
      alignPosition === AlignPosition.Bottom)
  );
};

const getOffsetFromSnap = (
  position: AssistantPositionX | AssistantPositionY,
  targetSize: number,
  alignTo: AlignPosition,
) => {
  if (alignPositionIsTheSameAsTheOpeningDirection(position, alignTo)) {
    return 0;
  }
  return targetSize * (isPositionTopOrLeft(position) ? 1 : -1);
};

const getDefaultOffset = (
  position: AssistantPositionX | AssistantPositionY,
) => {
  const BUFFER = 20;
  return BUFFER * (isPositionTopOrLeft(position) ? -1 : 1);
};

//adding the correct x offset so the tooltip will be aligned to the correct side of the target element
//if there is no alignment set - there will be a default (BUFFER) offset
const calculateAdditionalOffsetX = (
  positionX: AssistantPositionX,
  targetWidth: number,
  alignTo?: AlignPosition,
) => {
  return isSnappingToHorizontalSides(alignTo)
    ? getOffsetFromSnap(positionX, targetWidth, alignTo)
    : getDefaultOffset(positionX);
};
//also here just for the y offset
const calculateAdditionalOffsetY = (
  positionY: AssistantPositionY,
  targetHeight: number,
  alignTo?: AlignPosition,
) => {
  return isSnappingToVerticalSides(alignTo)
    ? getOffsetFromSnap(positionY, targetHeight, alignTo)
    : getDefaultOffset(positionY);
};

const getOuterPositionOffset = (
  element: HTMLElement,
  target: HTMLElement,
  positionX: AssistantPositionX = AssistantPositionX.Center,
  positionY: AssistantPositionY = AssistantPositionY.Center,
  offsetX = 0,
  offsetY = 0,
  alignTo?: AlignPosition,
): ClientRectOffset => {
  const targetBoundingClientRect = target.getBoundingClientRect();
  const offset: any = {};

  if (positionX === AssistantPositionX.Center) {
    offset.left =
      targetBoundingClientRect.left +
      targetBoundingClientRect.width / 2 -
      element.offsetWidth / 2;
  }

  if (positionY === AssistantPositionY.Center) {
    offset.top =
      targetBoundingClientRect.top -
      element.offsetHeight / 2 +
      targetBoundingClientRect.height / 2;
  }

  if (positionY === AssistantPositionY.Top) {
    offset.top =
      targetBoundingClientRect.top -
      element.offsetHeight +
      calculateAdditionalOffsetY(
        positionY,
        targetBoundingClientRect.height,
        alignTo,
      );
  }
  if (positionX === AssistantPositionX.Right) {
    offset.left =
      targetBoundingClientRect.right +
      calculateAdditionalOffsetX(
        positionX,
        targetBoundingClientRect.width,
        alignTo,
      );
  }
  if (positionY === AssistantPositionY.Bottom) {
    offset.top =
      targetBoundingClientRect.bottom +
      calculateAdditionalOffsetY(
        positionY,
        targetBoundingClientRect.height,
        alignTo,
      );
  }
  if (positionX === AssistantPositionX.Left) {
    offset.left =
      targetBoundingClientRect.left -
      element.offsetWidth +
      calculateAdditionalOffsetX(
        positionX,
        targetBoundingClientRect.width,
        alignTo,
      );
  }

  if (!isNaN(offset.top)) {
    offset.top += offsetY;
  }

  if (!isNaN(offset.left)) {
    offset.left += offsetX;
  }
  return offset;
};

const fitOffsetToScreen = (
  offset: ClientRectOffset,
  width: number,
  height: number,
) => {
  const bodyBoundingClientRect = document.body.getBoundingClientRect();
  let delta: number;
  if (offset.left + width > bodyBoundingClientRect.right) {
    delta = bodyBoundingClientRect.right - (offset.left + width);
    offset.left = offset.left + delta - 30;
  } else if (offset.left < bodyBoundingClientRect.left) {
    delta = bodyBoundingClientRect.left - offset.left;
    offset.left = offset.left + delta + 30;
  } else if (offset.top + height > bodyBoundingClientRect.bottom) {
    delta = bodyBoundingClientRect.bottom - (offset.top + height);
    offset.top += delta - 30;
  }

  return offset;
};

export const addPx = (original: string, add: number): string =>
  (parseInt(original.replace(/px/, ''), 10) + add).toString() + 'px';

export const nodesToArray = (nodes: NodeListOf<HTMLElement>) => {
  const arr: HTMLElement[] = [];
  nodes.forEach((x) => {
    arr.push(x);
  });
  return arr;
};
